import { cn } from "@/lib/utils";
import { Search, MapPin, Home, Layers, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SearchHeroProps {
  headline: string;
  subtext?: string;
  backgroundImage: string;
  backgroundVideo?: string;
}

const ChevronDown = () => (
  <svg className="w-3.5 h-3.5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const SearchHero = ({
  headline,
  subtext,
  backgroundImage,
  backgroundVideo,
}: SearchHeroProps) => {
  return (
    <section className="relative w-full overflow-hidden min-h-0 md:min-h-[90vh] flex items-center" aria-label="Property search">
      {/* Background media */}
      {backgroundVideo ? (
        <video
          autoPlay
          muted
          loop
          playsInline
          poster={backgroundImage}
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden="true"
        >
          <source src={backgroundVideo} type="video/mp4" />
        </video>
      ) : (
        <div className="absolute inset-0" aria-hidden="true">
          <img
            src={backgroundImage}
            alt=""
            className="w-full h-full object-cover scale-105"
            loading="eager"
          />
        </div>
      )}

      {/* Layered overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-rich-black/70 via-rich-black/45 to-rich-black/80" aria-hidden="true" />

      {/* Content */}
      <div className="relative z-10 w-full mx-auto max-w-wide px-4 sm:px-5 md:px-12 py-28 sm:py-36 md:py-44">
        {/* Headline group — generous spacing to search */}
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12 md:mb-16">
          <p className="font-body text-[11px] uppercase tracking-[0.3em] text-accent mb-4 md:mb-5">
            Massachusetts & Dominican Republic
          </p>
          <h1 className="font-headline text-4xl sm:text-5xl md:text-6xl lg:text-[5.25rem] xl:text-[5.75rem] text-cream leading-[1.05] tracking-tight">
            {headline}
          </h1>
          {subtext && (
            <p className="mt-5 md:mt-7 text-cream/60 text-sm md:text-base lg:text-lg leading-relaxed max-w-xl mx-auto">
              {subtext}
            </p>
          )}
        </div>

        {/* Search module — elevated container */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-card/97 backdrop-blur-xl shadow-[0_24px_80px_-12px_rgba(0,0,0,0.4)] border border-border/30 p-4 sm:p-6 md:p-8">

            {/* Primary search row — location is dominant */}
            <div className="mb-5">
              <label htmlFor="search-location" className="font-body text-base uppercase tracking-[0.12em] text-white/90 font-medium mb-3 block">
                Where are you looking?
              </label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-accent" aria-hidden="true" />
                <input
                  id="search-location"
                  type="text"
                  placeholder="Search by city, neighborhood, or address..."
                  className="w-full bg-background border-2 border-border pl-12 pr-5 py-4 font-body text-base text-foreground placeholder:text-muted-foreground focus:border-accent/50 focus:ring-4 focus:ring-accent/10 transition-all"
                />
              </div>
            </div>

            {/* Secondary filters row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              {/* Location */}
              <div>
                <label htmlFor="filter-location" className="font-body text-sm uppercase tracking-[0.1em] text-white/80 font-medium mb-2.5 block">
                  Location
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
                  <select id="filter-location" className="w-full appearance-none bg-background border border-border pl-9 pr-8 py-3 font-body text-sm text-foreground focus:ring-2 focus:ring-accent/20 focus:border-accent/40 cursor-pointer transition-all">
                    <option>All Locations</option>
                    <option>Boston, MA</option>
                    <option>Cambridge, MA</option>
                    <option>Cape Cod, MA</option>
                    <option>Punta Cana, DR</option>
                    <option>Santo Domingo, DR</option>
                    <option>Las Terrenas, DR</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <ChevronDown />
                  </div>
                </div>
              </div>

              {/* Category */}
              <div>
                <label htmlFor="filter-category" className="font-body text-sm uppercase tracking-[0.1em] text-white/80 font-medium mb-2.5 block">
                  Category
                </label>
                <div className="relative">
                  <Layers className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
                  <select id="filter-category" className="w-full appearance-none bg-background border border-border pl-9 pr-8 py-3 font-body text-sm text-foreground focus:ring-2 focus:ring-accent/20 focus:border-accent/40 cursor-pointer transition-all">
                    <option>All Categories</option>
                    <option>Residential</option>
                    <option>Investment</option>
                    <option>Luxury</option>
                    <option>New Development</option>
                    <option>Commercial</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <ChevronDown />
                  </div>
                </div>
              </div>

              {/* Property Type */}
              <div>
                <label htmlFor="filter-type" className="font-body text-sm uppercase tracking-[0.1em] text-white/80 font-medium mb-2.5 block">
                  Property Type
                </label>
                <div className="relative">
                  <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
                  <select id="filter-type" className="w-full appearance-none bg-background border border-border pl-9 pr-8 py-3 font-body text-sm text-foreground focus:ring-2 focus:ring-accent/20 focus:border-accent/40 cursor-pointer transition-all">
                    <option>All Types</option>
                    <option>Single Family</option>
                    <option>Condo</option>
                    <option>Townhouse</option>
                    <option>Villa</option>
                    <option>Penthouse</option>
                    <option>Land</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <ChevronDown />
                  </div>
                </div>
              </div>
            </div>

            {/* Search CTA */}
            <Button variant="gold" size="lg" className="w-full gap-2 sm:gap-3 text-sm sm:text-base h-12 sm:h-14" aria-label="Search properties">
              <Search className="w-5 h-5" aria-hidden="true" />
              Search Properties
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Button>

            {/* Quick filters */}
            <div className="mt-6 sm:mt-7 pt-5 sm:pt-6 border-t border-border/60 flex overflow-x-auto sm:flex-wrap items-center gap-2 sm:gap-2.5 scrollbar-hide -mx-1 px-1" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }} role="group" aria-label="Popular price filters">
              <span className="font-body text-xs uppercase tracking-[0.1em] text-white/75 font-medium mr-2" aria-hidden="true">Popular:</span>
              {["Under $500K", "$500K–$1M", "$1M–$3M", "$3M+", "Waterfront", "New Build"].map((tag) => (
                <button
                  key={tag}
                  className="font-body text-[10px] uppercase tracking-[0.14em] text-foreground/80 bg-background border border-border px-3 sm:px-4 py-1.5 sm:py-2 hover:border-accent hover:text-accent hover:bg-accent/5 focus:ring-2 focus:ring-accent/30 transition-all duration-200 whitespace-nowrap flex-shrink-0"
                  aria-label={`Filter by ${tag}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SearchHero;
